import React, { useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ArrowRight, Send, Loader, MessageCircle } from 'lucide-react';
import { addInquiry } from '../utils/productManager';
import SEO from './SEO';

const ContactPage: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        type: 'Living Room',
        budget: '50k - 1L',
        message: ''
    });

    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    useGSAP(() => {
        const tl = gsap.timeline();

        if (titleRef.current) {
            tl.from(titleRef.current.children, {
                y: 50,
                opacity: 0,
                duration: 1.2,
                stagger: 0.2,
                ease: 'power3.out'
            });
        }

        if (formRef.current) {
            tl.from(formRef.current.children, {
                y: 30,
                opacity: 0,
                duration: 0.8,
                stagger: 0.1,
                ease: 'power2.out'
            }, "-=0.5");
        }

    }, { scope: containerRef });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const saveToDb = async () => {
        return await addInquiry({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            type: formData.type,
            budget: formData.budget,
            message: formData.message,
        });
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            phone: '',
            type: 'Living Room',
            budget: '50k - 1L',
            message: ''
        });
    };

    const handleInquirySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);

        const saved = await saveToDb();

        if (saved) {
            setSuccess(true);
            resetForm();
        } else {
            alert("Something went wrong. Please try again.");
        }
        setSubmitting(false);
    };

    const handleWhatsAppSubmit = async () => {
        // Manual validation since this is a button click, not form submit
        if (!formData.name || !formData.phone) {
            alert("Please fill in at least your Name and Phone Number.");
            return;
        }

        setSubmitting(true);

        // Save first (fire and forget or wait? user said "added in database", probably implies wait to ensure capture)
        await saveToDb(); // We'll try to save, but proceed to WhatsApp even if it fails potentially? Or strict? Let's be strict for now or the user won't be captured.

        const text = `*New Web Inquiry*\n\n*Name:* ${formData.name}\n*Email:* ${formData.email}\n*Phone:* ${formData.phone}\n*Interest:* ${formData.type}\n*Budget:* ${formData.budget}\n*Message:* ${formData.message}`;
        const encodedText = encodeURIComponent(text);
        const whatsappUrl = `https://wa.me/919876511111?text=${encodedText}`;

        setSubmitting(false);
        resetForm();
        window.open(whatsappUrl, '_blank');
    };

    return (
        <div ref={containerRef} className="min-h-screen bg-diwan-900 text-diwan-cream pt-32 pb-24 px-6 md:px-12 flex items-center">
            <SEO
                title="Contact Us"
                description="Get in touch with D'mans Furniture to discuss your vision. Visit us in Chandigarh or contact us for bespoke luxury furniture."
                url="/contact"
            />
            <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-32">

                {/* Left Column: Text & Context */}
                <div ref={titleRef} className="space-y-8 self-center">
                    <span className="block text-diwan-gold uppercase tracking-[0.3em] text-xs font-bold">
                        Get in Touch
                    </span>
                    <h1 className="text-5xl md:text-7xl font-serif leading-tight">
                        Start Your <br />
                        <span className="text-white italic">Masterpiece.</span>
                    </h1>
                    <p className="text-diwan-cream/60 text-lg font-light leading-relaxed max-w-md">
                        We invite you to discuss your vision with us. Whether it's a single statement piece or a complete residence, let's create something timeless.
                    </p>

                    <div className="pt-8 space-y-4">
                        <div className="flex items-center gap-4 text-sm tracking-widest uppercase text-diwan-cream/40">
                            <span className="w-12 h-[1px] bg-diwan-gold"></span>
                            Direct Contact
                        </div>
                        <p className="text-xl font-serif">ranbirsinghdhimanandsons@gmail.com</p>
                        <p className="text-xl font-serif">+91 172 2792511</p>
                    </div>
                </div>

                {/* Right Column: Form */}
                <div className="bg-white/5 backdrop-blur-sm p-8 md:p-12 border border-white/10 rounded-sm min-h-[600px] flex flex-col justify-center">
                    {success ? (
                        <div className="text-center space-y-6 animate-in fade-in duration-500">
                            <div className="w-20 h-20 bg-diwan-gold rounded-full flex items-center justify-center mx-auto text-diwan-900 mb-6">
                                <Send size={32} />
                            </div>
                            <h3 className="text-3xl font-serif text-white">Thank You!</h3>
                            <p className="text-diwan-cream/70 text-lg leading-relaxed">
                                We have received your submission. <br />
                                Our team will reach out to you shortly.
                            </p>
                            <button
                                onClick={() => setSuccess(false)}
                                className="mt-8 px-8 py-3 border border-diwan-gold/30 text-diwan-gold hover:bg-diwan-gold hover:text-diwan-900 transition-colors uppercase tracking-widest text-xs font-bold"
                            >
                                Send Another
                            </button>
                        </div>
                    ) : (
                        <form ref={formRef} onSubmit={handleInquirySubmit} className="space-y-8">

                            <div className="space-y-6">
                                <div className="group">
                                    <label className="block text-xs uppercase tracking-widest text-diwan-gold mb-2">Your Name</label>
                                    <input
                                        required
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        type="text"
                                        className="w-full bg-transparent border-b border-diwan-cream/20 py-3 text-diwan-cream focus:outline-none focus:border-diwan-gold transition-colors text-lg"
                                        placeholder="John Doe"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="group">
                                        <label className="block text-xs uppercase tracking-widest text-diwan-gold mb-2">Phone</label>
                                        <input
                                            required
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            type="tel"
                                            className="w-full bg-transparent border-b border-diwan-cream/20 py-3 text-diwan-cream focus:outline-none focus:border-diwan-gold transition-colors text-lg"
                                            placeholder="+91..."
                                        />
                                    </div>
                                    <div className="group">
                                        <label className="block text-xs uppercase tracking-widest text-diwan-gold mb-2">Email</label>
                                        <input
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            type="email"
                                            className="w-full bg-transparent border-b border-diwan-cream/20 py-3 text-diwan-cream focus:outline-none focus:border-diwan-gold transition-colors text-lg"
                                            placeholder="john@example.com"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="group">
                                        <label className="block text-xs uppercase tracking-widest text-diwan-gold mb-2">Interest</label>
                                        <select
                                            name="type"
                                            value={formData.type}
                                            onChange={handleChange}
                                            className="w-full bg-transparent border-b border-diwan-cream/20 py-3 text-diwan-cream focus:outline-none focus:border-diwan-gold transition-colors text-lg appearance-none cursor-pointer"
                                        >
                                            <option className="bg-diwan-900" value="Living Room">Living Room</option>
                                            <option className="bg-diwan-900" value="Dining">Dining</option>
                                            <option className="bg-diwan-900" value="Bedroom">Bedroom</option>
                                            <option className="bg-diwan-900" value="Full Home">Full Home</option>
                                            <option className="bg-diwan-900" value="Commercial">Commercial</option>
                                        </select>
                                    </div>
                                    <div className="group">
                                        <label className="block text-xs uppercase tracking-widest text-diwan-gold mb-2">Budget</label>
                                        <select
                                            name="budget"
                                            value={formData.budget}
                                            onChange={handleChange}
                                            className="w-full bg-transparent border-b border-diwan-cream/20 py-3 text-diwan-cream focus:outline-none focus:border-diwan-gold transition-colors text-lg appearance-none cursor-pointer"
                                        >
                                            <option className="bg-diwan-900" value="Under 1L">Under 1 Lakh</option>
                                            <option className="bg-diwan-900" value="1L - 5L">1L - 5L</option>
                                            <option className="bg-diwan-900" value="5L - 10L">5L - 10L</option>
                                            <option className="bg-diwan-900" value="10L+">10L+</option>
                                            <option className="bg-diwan-900" value="Flexible">Flexible</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="group">
                                    <label className="block text-xs uppercase tracking-widest text-diwan-gold mb-2">Message</label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        rows={3}
                                        className="w-full bg-transparent border-b border-diwan-cream/20 py-3 text-diwan-cream focus:outline-none focus:border-diwan-gold transition-colors text-lg resize-none"
                                        placeholder="Tell us about your requirements..."
                                    ></textarea>
                                </div>
                            </div>

                            <div className="pt-4 flex flex-col md:flex-row gap-4 items-center justify-end">
                                {/* WhatsApp Button (Secondary) */}
                                <button
                                    type="button"
                                    onClick={handleWhatsAppSubmit}
                                    disabled={submitting}
                                    className="order-2 md:order-1 px-6 py-4 text-diwan-gold hover:text-white transition-colors flex items-center gap-2 group/wa disabled:opacity-50"
                                >
                                    <MessageCircle size={20} className="group-hover/wa:scale-110 transition-transform" />
                                    <span className="uppercase tracking-[0.2em] text-xs font-bold">Send via WhatsApp</span>
                                </button>

                                {/* Submit Inquiry Button (Primary) */}
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="order-1 md:order-2 w-full md:w-auto group flex items-center justify-center gap-4 px-8 py-4 bg-diwan-gold text-diwan-900 hover:bg-white transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader className="animate-spin" size={18} />
                                            <span className="uppercase tracking-[0.2em] text-xs font-bold">Processing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="uppercase tracking-[0.2em] text-xs font-bold">Send Inquiry</span>
                                            <Send size={18} className="group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContactPage;
